import { supabase } from '../supabase/client';
import { getNotificationTypeUrgent } from '../../utils/userNotificationPolicy';
import type { NotificationPriority } from '../../utils/userNotificationPolicy';

export type DispatchPushResult = {
  ok: boolean;
  sent?: number;
  failure?: number;
  skipped?: boolean;
  reason?: string;
  error?: string;
};

function mapDispatchReason(reason?: string): string {
  switch (reason) {
    case 'no_device_tokens':
      return '尚未註冊裝置。請允許系統通知後再開啟推播。';
    case 'fcm_not_configured':
      return '後端尚未設定 FCM（FCM_SERVICE_ACCOUNT_JSON）。';
    case 'blocked_by_preferences_or_dnd':
      return '推播已關閉，或目前在勿擾時段。';
    case 'duplicate':
      return '已發送（略過重複）';
    case 'invalid_channel':
      return '通知通道無效。';
    default:
      return reason ? `未送出：${reason}` : '未送出';
  }
}

export function formatDispatchPushResult(result: DispatchPushResult): string {
  if (result.ok && (result.sent ?? 0) > 0) {
    return `已發送到 ${result.sent} 部裝置`;
  }
  if (result.skipped) {
    return mapDispatchReason(result.reason);
  }
  return result.error || '發送失敗';
}

/** 站內通知建立後，請 Edge Function 發 FCM（App 鎖屏推播） */
export async function dispatchNativePushNotification(args: {
  userId: string;
  title: string;
  message: string;
  type: string;
  priority?: NotificationPriority;
}): Promise<DispatchPushResult> {
  try {
    const bypassPrefs =
      args.priority === 'urgent' || getNotificationTypeUrgent(args.type);

    const { data, error } = await supabase.functions.invoke('dispatch-push-notification', {
      body: {
        userId: args.userId,
        title: args.title,
        message: args.message,
        type: args.type,
        bypassPrefs,
      },
    });

    if (error) {
      console.warn('[dispatchNativePush]', error.message);
      return { ok: false, error: error.message };
    }

    const payload = (data ?? {}) as {
      sent?: number;
      failure?: number;
      skipped?: boolean;
      reason?: string;
      error?: string;
    };

    if (payload.error) {
      return { ok: false, error: payload.error, reason: payload.reason };
    }

    return {
      ok: !payload.skipped && (payload.sent ?? 0) > 0,
      sent: payload.sent,
      failure: payload.failure,
      skipped: payload.skipped,
      reason: payload.reason,
    };
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    console.warn('[dispatchNativePush] invoke failed:', e);
    return { ok: false, error: message };
  }
}
